import React, { useEffect, useMemo, useCallback, useRef, useState } from "react"
import debounce from "lodash.debounce"
import articleThemeExtension from "utils/articleThemeExtension"
import useEmailCapture from "../hooks/useEmailCapture"

import { Container, AdSlot, SignUpSlot } from "./styles"

import RightRailAd from "../ads/right-rail-ad"
import RightRailContentfulAd from "../ads/right-rail-contentful-ad"
import SignUpRightRail from "../sign-up-right-rail"

const RightRail = ({ article, contentfulAdUnits, showAds, adIndex, targetingOptions }) => {
  const { category, nsfw } = article
  const theme = articleThemeExtension(category)
  const emailCapture = useEmailCapture(article)
  const rightRailRef = useRef(null)
  const adsRendered = useRef(false)
  const [adSlots, setAdSlots] = useState([])
  const [adHeights, setAdHeights] = useState([])

  const contentfulAd = useMemo(
    () =>
      contentfulAdUnits?.find(
        (unit) => unit.device === "Desktop / Tablet" && unit.layout === "Vertical Rectangle",
      ),
    [contentfulAdUnits],
  )

  const renderAds = useCallback(() => {
    const maxAdCount = 6
    const maxAdHeight = 800
    const paddingHeight = 10
    const emailSignupPosition = 1 // Position where email signup should appear

    const rightRailElement = rightRailRef.current
    const rightRailHeight = rightRailElement?.getBoundingClientRect()?.height || 0

    let chunkHeight = 0
    let currentAdCount = maxAdCount
    while (currentAdCount !== 0) {
      chunkHeight = rightRailHeight / currentAdCount
      if (chunkHeight > maxAdHeight + paddingHeight) {
        break
      }
      currentAdCount -= 1
    }

    let enforceShortAd = false
    if (currentAdCount === 0 && rightRailHeight >= 360) {
      currentAdCount = 1
      enforceShortAd = true
    }

    // Pre-calculate heights
    const heights = Array(currentAdCount).fill(chunkHeight)
    setAdHeights(heights)

    // Create slots array with space for email signup
    const slots = Array(currentAdCount)
      .fill(null)
      .map((_, slotIdx) => ({
        index: adIndex.current + slotIdx,
        enforceShortAd,
        isEmailSignup: emailCapture && slotIdx === emailSignupPosition,
      }))
    setAdSlots(slots)
    adIndex.current += currentAdCount
  }, [adIndex, emailCapture])

  useEffect(() => {
    if (!showAds || !window.htlbid) return () => {}

    const rightRailElement = rightRailRef.current
    const hasRightRail = window.innerWidth >= theme.breakpoints.rightRail

    window.htlbid.cmd.push(() => {
      if (hasRightRail && !adsRendered.current) {
        renderAds()
        adsRendered.current = true
      }
    })

    const resizeObserver = new ResizeObserver(
      debounce(() => {
        const hasRightRail = window.innerWidth >= theme.breakpoints.rightRail
        if (hasRightRail && !adsRendered.current) {
          window.htlbid.cmd.push(() => {
            renderAds()
            adsRendered.current = true
          })
        }
      }, 500),
    )

    resizeObserver.observe(rightRailElement)

    return () => resizeObserver.unobserve(rightRailElement)
  }, [showAds, theme, renderAds])

  return (
    <Container ref={rightRailRef}>
      {showAds && contentfulAd && <RightRailContentfulAd element={contentfulAd} />}
      {adSlots.map((slot, idx) => (
        <React.Fragment key={slot.isEmailSignup ? "email-signup" : `ad-${slot.index}`}>
          {slot.isEmailSignup ? (
            <SignUpSlot height={adHeights[idx]}>
              <SignUpRightRail {...emailCapture} />
            </SignUpSlot>
          ) : (
            <AdSlot height={adHeights[idx]} visible={showAds}>
              <RightRailAd
                articleId={article.id}
                index={slot.index}
                nsfw={nsfw}
                enforceShortAd={slot.enforceShortAd}
              />
            </AdSlot>
          )}
        </React.Fragment>
      ))}
    </Container>
  )
}

export default React.memo(RightRail)
